import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { toast } from 'sonner';
import {
  Search,
  Plus,
  FileText,
  Mail,
  MessageSquare,
  Phone,
  Send,
  Eye,
  Edit,
  Copy,
  Clock,
  CheckCircle2,
  RefreshCw } from
'lucide-react';

const API_URL = API_BASE;

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

const channelIcons = {
  email: Mail,
  whatsapp: MessageSquare,
  sms: Phone,
  telegram: Send
};

const categoryColors = {
  ezparcel: 'bg-red-100 text-red-700 dark:bg-red-900/50',
  wms: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50',
  finance: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50',
  procurement: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50',
  crm: 'bg-teal-100 text-teal-700 dark:bg-teal-900/50',
  system: 'bg-gray-100 text-gray-700 dark:bg-gray-800'
};

export const MessageTemplates = () => {const { t } = useTranslation("comms");
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/communications/message-templates`;
      if (activeTab !== 'all') {
        url += `?category=${activeTab}`;
      }
      const response = await fetch(url, { headers: getAuthHeaders() });
      if (!response.ok) throw new Error('Failed to fetch templates');
      const data = await response.json();
      setTemplates(data);
    } catch (err) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, [activeTab]);

  const filteredTemplates = templates.filter((template) => {
    if (!searchTerm) return true;
    return template.name?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const categories = ['all', 'ezparcel', 'wms', 'finance', 'procurement', 'crm', 'system'];

  const stats = {
    total: templates.length,
    active: templates.filter((t) => t.status === 'active').length,
    multiChannel: templates.filter((t) => t.channels?.length > 1).length,
    totalUsage: templates.reduce((sum, t) => sum + (t.usageCount || 0), 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-violet-500" />
      </div>);

  }

  return (
    <div className="space-y-6" data-testid="communications-templates">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("messageTemplates", "Message Templates")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("manageNotificationTemplates", "Manage notification templates across all channels")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchTemplates} className="gap-2">
            <RefreshCw className="h-4 w-4" />{t("refresh", "Refresh")}

          </Button>
          <Button className="gap-2 bg-violet-600 hover:bg-violet-700">
            <Plus className="h-4 w-4" />{t("createTemplate", "Create Template")}

          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-4">
        <Card className="card-elevated border-l-4 border-l-violet-500">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">{t("totalTemplates", "Total Templates")}</p>
            <p className="text-2xl font-bold">{stats.total}</p>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-emerald-500">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">{t("active", "Active")}</p>
            <p className="text-2xl font-bold">{stats.active}</p>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">{t("multichannel", "Multi-Channel")}</p>
            <p className="text-2xl font-bold">{stats.multiChannel}</p>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-amber-500">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">{t("totalUsage", "Total Usage")}</p>
            <p className="text-2xl font-bold">{(stats.totalUsage ?? 0).toLocaleString()}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="card-elevated">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10" />
              
            </div>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="flex-wrap">
                {categories.map((cat) =>
                <TabsTrigger key={cat} value={cat} className="capitalize">
                    {cat === 'all' ? 'All' : cat}
                  </TabsTrigger>
                )}
              </TabsList>
            </Tabs>
          </div>
        </CardContent>
      </Card>

      {/* Templates Grid */}
      <div className="grid gap-4 md:grid-cols-2">
        {filteredTemplates.map((template) =>
        <Card key={template.id} className="card-elevated hover:shadow-lg transition-all">
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold">{template.name}</h3>
                    <Badge className={categoryColors[template.category] || categoryColors.system}>
                      {template.category}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground font-mono">{template.id}</p>
                </div>
                <Badge className={template.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'}>
                  {template.status === 'active' && <CheckCircle2 className="h-3 w-3 mr-1" />}
                  {template.status}
                </Badge>
              </div>

              {/* Channels */}
              <div className="flex gap-2 mb-3">
                {template.channels?.map((channel) => {
                const Icon = channelIcons[channel] || Mail;
                return (
                  <div key={channel} className={`p-2 rounded-lg ${
                  channel === 'email' ? 'bg-violet-100 dark:bg-violet-900/30' :
                  channel === 'whatsapp' ? 'bg-emerald-100 dark:bg-emerald-900/30' :
                  channel === 'sms' ? 'bg-orange-100 dark:bg-orange-900/30' :
                  'bg-blue-100 dark:bg-blue-900/30'}`
                  }>
                      <Icon className={`h-4 w-4 ${
                    channel === 'email' ? 'text-violet-600' :
                    channel === 'whatsapp' ? 'text-emerald-600' :
                    channel === 'sms' ? 'text-orange-600' :
                    'text-blue-600'}`
                    } />
                    </div>);

              })}
              </div>

              {/* Preview */}
              <div className="p-3 rounded-lg bg-muted/30 mb-3">
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {template.content?.whatsapp || template.content?.sms || template.content?.telegram || template.content?.email?.subject || 'No preview available'}
                </p>
              </div>

              {/* Variables */}
              <div className="flex flex-wrap gap-1 mb-3">
                {template.variables?.slice(0, 4).map((variable) =>
              <Badge key={variable} variant="outline" className="text-xs font-mono">
                    {`{{${variable}}}`}
                  </Badge>
              )}
                {template.variables?.length > 4 &&
              <Badge variant="outline" className="text-xs">
                    +{template.variables.length - 4}{t("more", "more")}
              </Badge>
              }
              </div>

              {/* Stats & Actions */}
              <div className="flex items-center justify-between pt-3 border-t border-border">
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span>{(template.usageCount || 0).toLocaleString()}{t("uses", "uses")}</span>
                  {template.lastUsed &&
                <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      <span>{new Date(template.lastUsed).toLocaleDateString()}</span>
                    </div>
                }
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {filteredTemplates.length === 0 &&
      <Card className="card-elevated">
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">{t("noTemplatesFound", "No templates found")}</h3>
            <p className="text-muted-foreground">{t("tryAdjustingYour", "Try adjusting your search or filter criteria")}</p>
          </CardContent>
        </Card>
      }
    </div>);

};